// Text preview with highlight-to-mark PII — select text and classify it
import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import type { PIIType } from '@/types/pii'

interface PIITextHighlighterProps {
  text: string
  onAddPII: (original: string, type: PIIType) => void
}

const QUICK_TYPES: { value: PIIType; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
  { value: 'ssn', label: 'SSN/ID' },
  { value: 'bank_account', label: 'Bank' },
  { value: 'company_contact', label: 'Contact' },
  { value: 'other', label: 'Other' },
]

export function PIITextHighlighter({ text, onAddPII }: PIITextHighlighterProps): JSX.Element {
  const [selectedText, setSelectedText] = useState('')
  const [showClassify, setShowClassify] = useState(false)
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 })

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection()
    const selected = selection?.toString().trim() || ''
    if (selected.length >= 2) {
      setSelectedText(selected)
      const range = selection?.getRangeAt(0)
      if (range) {
        const rect = range.getBoundingClientRect()
        setPopupPos({ x: rect.left, y: rect.bottom + 4 })
      }
      setShowClassify(true)
    } else {
      setShowClassify(false)
      setSelectedText('')
    }
  }, [])

  const handleClassify = (type: PIIType) => {
    onAddPII(selectedText, type)
    setShowClassify(false)
    setSelectedText('')
    window.getSelection()?.removeAllRanges()
  }

  return (
    <div className="relative">
      <p className="mb-2 text-xs text-kaycoo-charcoal-2">
        <Plus className="mr-1 inline h-3 w-3" />
        Select text below to mark as PII
      </p>
      <pre
        onMouseUp={handleMouseUp}
        className="cursor-text whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-kaycoo-charcoal leading-relaxed select-text"
      >
        {text}
      </pre>

      {showClassify && selectedText && (
        <div
          className="fixed z-[60] rounded-lg border bg-white p-2 shadow-lg"
          style={{ left: `${Math.min(popupPos.x, window.innerWidth - 320)}px`, top: `${popupPos.y}px` }}
        >
          <p className="mb-1.5 text-xs text-kaycoo-charcoal-2">
            Mark "<span className="font-medium text-kaycoo-charcoal">{selectedText.length > 30 ? selectedText.slice(0, 30) + '...' : selectedText}</span>" as:
          </p>
          <div className="flex flex-wrap gap-1">
            {QUICK_TYPES.map((t) => (
              <Button key={t.value} size="sm" variant="outline"
                onClick={() => handleClassify(t.value)}
                className="h-6 px-2 text-[10px]">
                {t.label}
              </Button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => setShowClassify(false)}
            className="mt-1 h-5 w-full text-[10px] text-kaycoo-charcoal-2">
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}
